import { useEffect, useState, useRef } from 'react';

interface UseDelayedLoadingStateOptions<T> {
  /**
   * The current value to display
   */
  value: T | undefined;
  /**
   * Whether a request is currently pending
   */
  isLoading: boolean;
  /**
   * Delay in milliseconds before considering the state as loading (default: 500)
   */
  delay?: number;
  /**
   * When true, the first (initial) load shows immediately without waiting for the delay.
   * Subsequent loads still use the delay. Defaults to false.
   */
  shouldSkipInitialDelay?: boolean;
}

interface UseDelayedLoadingStateResult<T> {
  /**
   * The value that should be displayed.
   * While loading, this will prefer the last non-empty value if available.
   */
  displayValue: T | undefined;
  /**
   * Delayed loading flag: becomes true only if the pending state
   * lasts longer than the configured delay.
   * When `shouldSkipInitialDelay` is true, the initial load is shown immediately.
   */
  isLoading: boolean;
}

/**
 * Hook that manages delayed loading state while preserving the previous value.
 */
export const useDelayedLoadingState = <T>({
  value,
  isLoading,
  delay = 500,
  shouldSkipInitialDelay = false,
}: UseDelayedLoadingStateOptions<T>): UseDelayedLoadingStateResult<T> => {
  const isInitialLoadRef = useRef(true);
  const [localIsLoading, setLocalIsLoading] = useState(shouldSkipInitialDelay ? isLoading : false);

  // Stores the last meaningful value
  const previousValueRef = useRef<T | undefined>(value);

  // Update previous value when the new value is meaningful
  useEffect(() => {
    if (value !== undefined && value !== '') {
      previousValueRef.current = value;
    }
  }, [value]);

  // Delayed loading flag
  useEffect(() => {
    if (isLoading) {
      if (shouldSkipInitialDelay && isInitialLoadRef.current) {
        setLocalIsLoading(true);
        return;
      }

      const timeoutId = setTimeout(() => {
        setLocalIsLoading(true);
      }, delay);

      return () => {
        clearTimeout(timeoutId);
        setLocalIsLoading(false);
      };
    }

    isInitialLoadRef.current = false;
    setLocalIsLoading(false);
  }, [isLoading, delay, shouldSkipInitialDelay]);

  // Prefer the current value when available
  if (value !== undefined && value !== '') {
    return { displayValue: value, isLoading: localIsLoading };
  }

  // Fall back to previous value
  return {
    displayValue: previousValueRef.current,
    isLoading: localIsLoading,
  };
};
